import { styled } from '@mui/material/styles';
import { number, object, shape, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import ProgressiveImage from 'react-progressive-image';
import getImageDimensions, { isExtremePreviewRatio } from 'utils/getImageDimensions';
import connector from './connector';

const PictureLink = styled('a', {
  shouldForwardProp: prop => prop !== 'src',
})(({ theme, src }) => ({
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  justifyContent: 'center',
  '&::before': {
    position: 'absolute',
    content: `''`,
    backgroundImage: `url(${src})`,
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    filter: 'blur(30px)',
    transform: 'scale(1.3)',
    backgroundColor: theme.palette.black,
    backfaceVisibility: 'hidden',
  },
  '&>img': {
    zIndex: 1,
  },
}));

const PictureThumbnail = ({
  actions,
  src: imgSrc,
  fullSrc = null,
  meta,
  maxWidth = null,
  maxHeight = null,
}) => {
  const openSrc = fullSrc || imgSrc;
  const openPicture = event => {
    actions.pictureModal.open(openSrc);
    event.preventDefault();
  };
  const [dimensions, setDimensions] = useState(getImageDimensions(meta));
  useEffect(() => {
    setDimensions(getImageDimensions(meta));
  }, [meta]);
  let { width, height } = dimensions;
  if (maxWidth && width >= height && maxWidth < width) {
    const scale = maxWidth / width;
    width *= scale;
    height *= scale;
  }
  if (maxHeight && width <= height && maxHeight < height) {
    const scale = maxHeight / height;
    width *= scale;
    height *= scale;
  }
  const [srcW, srcH] = String(meta?.imageSize || '')
    .split('x')
    .map(Number);
  const objectFit = isExtremePreviewRatio(srcW, srcH) ? 'cover' : 'contain';
  return (
    <PictureLink href={openSrc} src={meta?.placeholder || imgSrc} onClick={openPicture}>
      <ProgressiveImage style={{ width, height }} src={imgSrc} placeholder={meta?.placeholder}>
        {(src, loading) => (
          // eslint-disable-next-line jsx-a11y/alt-text
          <img
            src={src}
            style={{
              width,
              height,
              objectFit,
              ...(loading
                ? {
                    cursor: 'pointer',
                    filter: 'blur(4px)',
                    transform: 'scale(1.05)',
                  }
                : {
                    backgroundImage: 'url(/icons/transparent-background.svg)',
                    borderRadius: 0,
                  }),
            }}
          />
        )}
      </ProgressiveImage>
    </PictureLink>
  );
};

PictureThumbnail.propTypes = {
  src: string.isRequired,
  fullSrc: string,
  actions: shape({
    pictureModal: object.isRequired,
  }).isRequired,
  meta: object,
  maxWidth: number,
  maxHeight: number,
};

export default connector(PictureThumbnail);
